/**
 * Right-clicking a CONTAINER on the bench: what you can do to that piece of
 * glassware — reshape it, put a burner under it, run a delivery tube from it,
 * take it away. Nothing about
 * the stand it is sitting in, which is a different object with a menu of its
 * own (StandContextMenu); a menu that offered everything on the bench would
 * make the two objects indistinguishable.
 *
 * Positioned at the click point; dismissed by clicking elsewhere or pressing
 * Escape. These act ON the container you right-clicked, not on whichever one
 * happens to be selected.
 */
import { useRef } from 'react';
import { VESSEL_TYPES, VESSEL_LABELS, type VesselType } from '../vesselTypes';
import { BURNER_TYPES, BURNER_HINTS, BURNER_LABELS, type BurnerType } from '../burnerTypes';
import { BurnerIcon, VesselGlyph } from './ApparatusIcons';
import { useDismiss, useAnchoredPosition } from './contextMenu';

interface ContainerContextMenuProps {
  x: number;
  y: number;
  currentVesselType: VesselType;
  onChangeVesselType: (type: VesselType) => void;
  burnerType: BurnerType;
  /** Whether a burner could physically reach this vessel — false in a wooden
   *  tube rack, and for anything clamped above another vessel. */
  canHeat: boolean;
  onChangeBurner: (burner: BurnerType) => void;
  /** Other containers a delivery tube could run to, already excluding any that
   *  would make a loop. */
  pipeTargets: { id: string; label: string }[];
  /** Where a tube already runs from here, if one does. */
  pipedToLabel?: string;
  onConnectPipe: (toId: string) => void;
  onDisconnectPipe: () => void;
  /** Whether this container has a rod resting in it, dipped in a reagent —
   *  the section below only makes sense once there is something on the rod
   *  to bring near a gas. */
  hasGlassRod: boolean;
  /** Other containers currently giving off a gas, the rod could be tested
   *  against. */
  gasTestTargets: { id: string; label: string }[];
  onHoldRodNear: (toId: string) => void;
  /** Whether there is anything in it worth swirling — an empty vessel swirls
   *  to no visible effect, which reads as a broken control. */
  canSwirl: boolean;
  onSwirl: () => void;
  /** Whether there is anything in it to pour away. */
  canClear: boolean;
  onClear: () => void;
  canRemove: boolean;
  onRemove: () => void;
  onClose: () => void;
}

export function ContainerContextMenu({
  x, y, currentVesselType, onChangeVesselType,
  burnerType, canHeat, onChangeBurner, pipeTargets, pipedToLabel, onConnectPipe, onDisconnectPipe,
  hasGlassRod, gasTestTargets, onHoldRodNear,
  canSwirl, onSwirl, canClear, onClear, canRemove, onRemove, onClose,
}: ContainerContextMenuProps): React.JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  useDismiss(ref, onClose);
  const pos = useAnchoredPosition(ref, x, y);

  return (
    <div ref={ref} className="chemlab-context-menu" style={pos}>
      {/* Reshaping the glassware in place is a desktop convenience — on a
          phone the same four shapes are one tap away in the Apparatus pane,
          and the menu has to earn every row it costs. Hidden in the head, not
          here, so there is one menu in the markup. */}
      <div className="chemlab-context-menu-shape">
        <p className="chemlab-context-menu-hint">Shape</p>
        <div className="chemlab-context-menu-vessels">
          {VESSEL_TYPES.map(type => (
            <button
              key={type}
              className={`chemlab-context-menu-vessel${type === currentVesselType ? ' active' : ''}`}
              title={VESSEL_LABELS[type]}
              onClick={() => { onChangeVesselType(type); onClose(); }}
            >
              <VesselGlyph vessel={type} size={20} />
            </button>
          ))}
        </div>
        <div className="chemlab-context-menu-divider" />
      </div>
      <p className="chemlab-context-menu-hint">Burner</p>
      <div className="chemlab-context-menu-vessels">
        {BURNER_TYPES.map(burner => (
          <button
            key={burner}
            className={`chemlab-context-menu-vessel${burner === burnerType ? ' active' : ''}`}
            disabled={!canHeat}
            title={burner === burnerType
              ? `Take the ${BURNER_LABELS[burner].toLowerCase()} away`
              : `${BURNER_LABELS[burner]} — ${BURNER_HINTS[burner]}`}
            /* The lit one is a toggle: pressing the burner that is already
               under this vessel takes it away. It is drawn pressed, so
               pressing it again meaning "keep it" is the one thing it cannot
               sensibly mean — and removing a burner had no control at all. */
            onClick={() => { onChangeBurner(burner === burnerType ? 'none' : burner); onClose(); }}
          >
            <BurnerIcon burner={burner} size={20} />
          </button>
        ))}
      </div>
      {/* Only when there is something to say: which burner is lit, or why the
          two above it cannot be. Telling someone to tap a burner is a caption
          on a row of burners — the one thing the row already says itself. */}
      {(!canHeat || burnerType !== 'none') && (
        <p className="chemlab-context-menu-hint">
          {canHeat ? BURNER_LABELS[burnerType] : 'Nothing standing on the bench can reach this one.'}
        </p>
      )}
      {/* A delivery tube carries the gas THIS vessel gives off into another
          one — the CO2-into-limewater test, and every gas test built like it.
          The section is here only when there is a tube to run or to cut: a
          lone container on the bench used to get a line explaining what it
          could do once it wasn't alone, which is a menu entry for a menu
          entry that isn't there. */}
      {pipedToLabel ? (
        <>
          <div className="chemlab-context-menu-divider" />
          <p className="chemlab-context-menu-hint">Gas goes to {pipedToLabel}</p>
          <button className="chemlab-context-menu-item" onClick={() => { onDisconnectPipe(); onClose(); }}>
            ✂️ Disconnect the Tube
          </button>
        </>
      ) : pipeTargets.length > 0 && (
        <>
          <div className="chemlab-context-menu-divider" />
          <p className="chemlab-context-menu-hint">Run a Delivery Tube To</p>
          {pipeTargets.map(target => (
            <button
              key={target.id}
              className="chemlab-context-menu-item"
              onClick={() => { onConnectPipe(target.id); onClose(); }}
            >
              〰️ {target.label}
            </button>
          ))}
        </>
      )}

      {/* The rod carries whatever it was dipped in — held near another
          vessel's escaping gas, the two can react at the rod's tip without
          either ever touching the other's liquid. The ammonia/HCl "dense
          white fumes" test is the classic version of this. */}
      {hasGlassRod && (
        <>
          <div className="chemlab-context-menu-divider" />
          {gasTestTargets.length > 0 ? (
            <>
              <p className="chemlab-context-menu-hint">Hold the Rod Near</p>
              {gasTestTargets.map(target => (
                <button
                  key={target.id}
                  className="chemlab-context-menu-item"
                  onClick={() => { onHoldRodNear(target.id); onClose(); }}
                >
                  💨 {target.label}
                </button>
              ))}
            </>
          ) : (
            <p className="chemlab-context-menu-hint">
              Nothing on the bench is giving off a gas to test the rod against.
            </p>
          )}
        </>
      )}

      <div className="chemlab-context-menu-divider" />
      {/* Swirling is what a hand does constantly at a real bench and the bench
          had no way to say. It mixes; it does not react — see
          chemistry-lab-dosing-target.md. */}
      <button
        className="chemlab-context-menu-item"
        disabled={!canSwirl}
        onClick={() => { onSwirl(); onClose(); }}
      >
        🌀 Swirl It
      </button>
      <button
        className="chemlab-context-menu-item"
        disabled={!canClear}
        onClick={() => { onClear(); onClose(); }}
      >
        🚿 Empty It Out
      </button>
      <button
        className="chemlab-context-menu-item chemlab-context-menu-remove"
        disabled={!canRemove}
        onClick={() => { onRemove(); onClose(); }}
      >
        🗑️ Remove Container
      </button>
      {!canRemove && <p className="chemlab-context-menu-hint chemlab-context-menu-remove-hint">At least one container must stay on the bench.</p>}
    </div>
  );
}
